// src/pages/SalarySmsSheet.jsx — responsive cards + table hybrid, actions (Copy SMS, CSV, Print)
// Enhanced: Branch/Staff header, Section 1: Applied Options, Section 2: Earnings (gross),
// Deductions with totals, and Advance Salary History (categorized)
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Tooltip,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Skeleton,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime'; // Half day
import MoneyOffIcon from '@mui/icons-material/MoneyOff'; // No tips
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'; // No service
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiUrl } from '../config/api';
import { APP_NAME, BRANCH_NAME } from '../config/branding';
import { toBranchDisplayName } from '../config/branchNames';
import { formatSriLankaDateLabel, formatSriLankaDateTimeLabel } from '../lib/sriLankaTime';

const fmt = (n) => Number(n || 0).toFixed(2);
const money = (n) => `Rs ${fmt(n)}`;
const fmtDayCount = (n) => {
  const value = Number(n || 0);
  if (!Number.isFinite(value)) return '0';
  return value % 1 === 0 ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
};
const approxEq = (a, b, eps = 0.01) => Math.abs(Number(a) - Number(b)) <= eps;
const isPunishmentHalfFlag = (row) => !!row?.flag_punishment_half;
const punishmentHalfLabel = 'Punishment Half Day';
const RECEIPT_THEME = Object.freeze({
  primary: [27, 94, 74],
  primarySoft: [236, 244, 241],
  success: [21, 128, 61],
  danger: [185, 28, 28],
  border: [207, 216, 220],
  ink: [22, 28, 36],
  muted: [93, 103, 113],
  surface: [249, 250, 251],
  surfaceAlt: [243, 246, 248],
});
const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');
const formatReceiptMoneyLabel = (value, isMoney = true) => {
  const text = String(value ?? '').trim();
  if (!isMoney) return text;
  if (!text) return 'Rs 0.00';
  if (text.startsWith('+')) return `+ Rs ${text.replace(/^\+\s*/, '')}`;
  if (text.startsWith('-')) return `- Rs ${text.replace(/^-\s*/, '')}`;
  return `Rs ${text}`;
};
const PUBLIC_SALARY_SHEET_API = apiUrl('salary_sms_sheet_data');

function getDailyFlagsText(dayRow, perDay) {
  const flagHalf =
    typeof dayRow?.flag_half !== 'undefined'
      ? !!dayRow.flag_half
      : perDay > 0 && approxEq(dayRow?.daily_basic, perDay / 2);
  const flagPunishmentHalf = isPunishmentHalfFlag(dayRow);
  const flagNoTips =
    typeof dayRow?.flag_no_tips !== 'undefined'
      ? !!dayRow.flag_no_tips
      : Number(dayRow?.daily_tip) === 0;
  const flagNoSvc =
    typeof dayRow?.flag_no_svc !== 'undefined'
      ? !!dayRow.flag_no_svc
      : Number(dayRow?.daily_svc) === 0;

  return [
    flagPunishmentHalf ? 'Punishment Half' : (flagHalf ? 'Half Day' : 'Full Day'),
    flagNoTips ? 'No Tips' : null,
    flagNoSvc ? 'No Service' : null,
  ].filter(Boolean).join(' | ');
}

export default function SalarySmsSheet({ documentLabel = 'Salary Sheet' }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [search] = useSearchParams();
  const staffId = search.get('staff_id');
  const year = search.get('year');
  const month = search.get('month');
  const branchIdQuery = Number(search.get('branch_id') || 0);

  const [slip, setSlip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const reload = useCallback(() => {
    if (!staffId || !year || !month) {
      setError('Missing query parameters.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    const params = new URLSearchParams({
      staff_id: String(staffId),
      year: String(year),
      month: String(month),
    });
    if (branchIdQuery > 0) {
      params.set('branch_id', String(branchIdQuery));
    }

    fetch(`${PUBLIC_SALARY_SHEET_API}?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        if (data?.status === 'ok' && data?.item) {
          setSlip(data.item);
          return;
        }
        setSlip(null);
        setError(data?.message || 'No record found.');
      })
      .catch(() => setError('Failed to load data.'))
      .finally(() => setLoading(false));
  }, [staffId, year, month, branchIdQuery]);

  useEffect(() => { reload(); }, [reload]);

  const rows = useMemo(
    () => (slip && Array.isArray(slip.daily_breakdown) ? slip.daily_breakdown : []),
    [slip]
  );

  /* -------- Applied-options summary (counts + tips/service totals) -------- */
  const applied = useMemo(() => {
    if (!slip) return null;
    const perDay = Number(slip.basic_salary_per_day || 0);

    let halfDays = 0;
    let normalHalfDays = 0;
    let punishmentHalfDays = 0;
    let noTipDays = 0;
    let noSvcDays = 0;
    let tipTotal = 0;
    let svcTotal = 0;
    let punishmentHalfDayFund = 0;

    rows.forEach((d) => {
      const flagHalf =
        typeof d.flag_half !== 'undefined' ? !!d.flag_half : perDay > 0 && approxEq(d.daily_basic, perDay / 2);
      const flagPunishmentHalf = isPunishmentHalfFlag(d);
      const flagNoTips = typeof d.flag_no_tips !== 'undefined' ? !!d.flag_no_tips : Number(d.daily_tip) === 0;
      const flagNoSvc = typeof d.flag_no_svc !== 'undefined' ? !!d.flag_no_svc : Number(d.daily_svc) === 0;
      if (flagHalf) halfDays++;
      if (flagPunishmentHalf) punishmentHalfDays++;
      if (flagHalf && !flagPunishmentHalf) normalHalfDays++;
      if (flagNoTips) noTipDays++;
      if (flagNoSvc) noSvcDays++;
      tipTotal += Number(d.daily_tip) || 0;
      svcTotal += Number(d.daily_svc) || 0;
      punishmentHalfDayFund += Number(d.punishment_half_fund || 0);
    });

    const daysPresent = Number(slip.days_present || 0);
    const fullDays = Number(slip.full_days ?? Math.max(0, daysPresent - halfDays));
    const eligibleDays = Number(slip.eligible_days ?? Math.max(0, daysPresent - (halfDays * 0.5)));
    return {
      halfDays,
      normalHalfDays: Number(slip.normal_half_days ?? normalHalfDays),
      punishmentHalfDays: Number(slip.punishment_half_days ?? punishmentHalfDays),
      fullDays,
      eligibleDays,
      noTipDays,
      noSvcDays,
      tipTotal,
      svcTotal,
      perDay,
      punishmentHalfDayFund: Number(slip.punishment_half_day_fund ?? punishmentHalfDayFund),
    };
  }, [slip, rows]);

  /* -------- Earnings & Deductions math -------- */
  const earnings = useMemo(() => {
    if (!slip) return null;
    const basic = Number(slip.basic_earning || 0);
    const bonus = Number(slip.bonus_total || 0);
    const tips = Number(applied?.tipTotal || 0);
    const svc = Number(applied?.svcTotal || 0);
    const daysPresent = Number(slip.days_present || 0);
    const eligibleDays = Number(applied?.eligibleDays || 0);
    const gross = basic + bonus + tips + svc;
    return {
      daysPresent,
      eligibleDays,
      basic,
      bonus,
      tips,
      svc,
      gross,
      punishmentHalfDayFund: Number(applied?.punishmentHalfDayFund || 0),
    };
  }, [slip, applied]);

  const deductions = useMemo(() => {
    if (!slip) return null;
    const loan = Number(slip.loan_deduction || 0);
    const penalty = Number(slip.penalty_total || 0);
    const advance = Number(slip.advance_deduction || 0);
    const total = loan + penalty + advance;
    return { loan, penalty, advance, total };
  }, [slip]);

  // Show a mismatch indicator if API net != computed
  const calcNet = useMemo(() => {
    if (!earnings || !deductions) return null;
    return earnings.gross - deductions.total;
  }, [earnings, deductions]);

  const apiNet = Number(slip?.total_salary || 0);
  const netMismatch = calcNet != null && !approxEq(calcNet, apiNet);

  const mStr = String(month || '').padStart(2, '0');
  const displayBranch = toBranchDisplayName({
    branchId: slip?.branch_id || branchIdQuery,
    branchName: slip?.branch_name || slip?.branch || BRANCH_NAME,
    branchCode: slip?.branch_code || '',
  });

  useEffect(() => {
    if (!year || !month) return;
    document.title = `${APP_NAME} ${documentLabel} | ${String(month).padStart(2, '0')}/${year}`;
  }, [documentLabel, year, month]);

  /* -------- Advance history (categorized) -------- */
  const advanceHistory = useMemo(() => {
    // try several likely property names
    const list =
      (slip && Array.isArray(slip.advance_payments) && slip.advance_payments) ||
      (slip && Array.isArray(slip.advance_history) && slip.advance_history) ||
      (slip && Array.isArray(slip.advances) && slip.advances) ||
      [];
    // Normalize each item
    const norm = list.map((x) => ({
      date: x.date || x.advance_date || x.txn_date || x.created_at || '',
      amount: Number(x.amount || x.value || 0),
      category: x.category || x.type || 'Advance Salary',
      note: x.note || x.remark || '',
    }))
      .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
    // Group by category
    const groups = norm.reduce((acc, it) => {
      (acc[it.category] = acc[it.category] || []).push(it);
      return acc;
    }, {});
    // Build totals per category
    const catTotals = Object.fromEntries(
      Object.entries(groups).map(([k, arr]) => [k, arr.reduce((s, i) => s + (i.amount || 0), 0)])
    );
    const grand = norm.reduce((s, i) => s + (i.amount || 0), 0);
    return { groups, catTotals, grand, flat: norm };
  }, [slip]);

  const receiptModel = useMemo(() => {
    if (!slip) return null;

    const perDay = Number(slip.basic_salary_per_day || 0);
    const generatedAt = formatSriLankaDateTimeLabel(new Date());

    const stats = [
      { label: 'Attendance', value: fmtDayCount(earnings?.daysPresent ?? 0) },
      { label: 'Eligible', value: fmtDayCount(earnings?.eligibleDays ?? 0) },
      { label: 'Full Days', value: fmtDayCount(applied?.fullDays ?? 0) },
      { label: 'Half Days', value: fmtDayCount(applied?.halfDays ?? 0) },
    ];

    const earningsRows = [
      { label: 'Basic Earned', value: fmt(earnings?.basic), tone: 'base', isMoney: true },
      { label: 'Tips Share', value: fmt(earnings?.tips), tone: 'base', isMoney: true },
      { label: 'Service Share', value: fmt(earnings?.svc), tone: 'base', isMoney: true },
      { label: 'Bonus', value: `+ ${fmt(earnings?.bonus)}`, tone: 'positive', isMoney: true },
      { label: 'Gross Total', value: fmt(earnings?.gross), tone: 'total', isMoney: true },
    ];

    const deductionRows = [
      { label: 'Penalty', value: `- ${fmt(deductions?.penalty)}`, tone: 'negative', isMoney: true },
      { label: 'Loan', value: `- ${fmt(deductions?.loan)}`, tone: 'negative', isMoney: true },
      { label: 'Advance', value: `- ${fmt(deductions?.advance)}`, tone: 'negative', isMoney: true },
      { label: 'Total Deductions', value: fmt(deductions?.total), tone: 'total', isMoney: true },
    ];

    const optionRows = [
      { label: 'Normal Half Days', value: fmtDayCount(applied?.normalHalfDays ?? 0), isMoney: false },
      { label: punishmentHalfLabel, value: fmtDayCount(applied?.punishmentHalfDays ?? 0), isMoney: false },
      { label: 'No Tips Days', value: String(applied?.noTipDays ?? 0), isMoney: false },
      { label: 'No Service Days', value: String(applied?.noSvcDays ?? 0), isMoney: false },
      { label: 'Tips Total', value: fmt(applied?.tipTotal ?? 0), isMoney: true },
      { label: 'Service Total', value: fmt(applied?.svcTotal ?? 0), isMoney: true },
      { label: 'Punishment Half To Fund', value: fmt(applied?.punishmentHalfDayFund ?? 0), isMoney: true },
    ];

    const advanceRows = advanceHistory.flat.map((item) => ({
      date: formatSriLankaDateLabel(item.date),
      amount: fmt(item.amount),
      details: [
        item.category && item.category !== 'Advance Salary' ? item.category : '',
        item.note || '',
      ].filter(Boolean).join(' | ') || '-',
    }));

    const dailyRows = rows.map((dayRow) => ({
      date: formatSriLankaDateLabel(dayRow.date),
      amount: fmt(dayRow.daily_total),
      details: [
        `B ${fmt(dayRow.daily_basic)}`,
        `T ${fmt(dayRow.daily_tip)}`,
        `S ${fmt(dayRow.daily_svc)}`,
        getDailyFlagsText(dayRow, perDay),
      ].join(' | '),
    }));

    return {
      generatedAt,
      title: `${APP_NAME} ${documentLabel}`,
      staffName: String(slip.staff_name || ''),
      branchName: displayBranch,
      periodLabel: `${mStr}/${year}`,
      grossTotal: fmt(earnings?.gross),
      netPay: fmt(apiNet),
      netTone: apiNet < 0 ? 'negative' : 'positive',
      stats,
      earningsRows,
      deductionRows,
      optionRows,
      advanceRows,
      dailyRows,
    };
  }, [slip, earnings, deductions, applied, advanceHistory, rows, displayBranch, mStr, year, apiNet, documentLabel]);

  const handleCopySMS = () => {
    if (!slip) return;
    const lines = [
      `${APP_NAME} ${documentLabel} — ${slip.staff_name} (${mStr}/${year}) [${displayBranch}]`,
      `Days: ${earnings?.daysPresent ?? 0}`,
      `Punishment Half To Fund: ${money(earnings?.punishmentHalfDayFund)}`,
      `Basic: ${money(earnings?.basic)}`,
      `Tips: ${money(earnings?.tips)}`,
      `Service: ${money(earnings?.svc)}`,
      `Bonus: ${money(earnings?.bonus)}`,
      `Gross: ${money(earnings?.gross)}`,
      `Penalties: ${money(deductions?.penalty)}`,
      `Loan: ${money(deductions?.loan)}`,
      `Advance: ${money(deductions?.advance)}`,
      `Deductions: ${money(deductions?.total)}`,
      `Net Pay: ${money(apiNet)}${netMismatch ? ` (calc ${money(calcNet)})` : ''}`,
    ];
    const text = lines.join('\n');
    navigator.clipboard
      .writeText(text)
      .then(() => setSnack({ open: true, msg: 'Summary copied to clipboard', severity: 'success' }))
      .catch(() => setSnack({ open: true, msg: 'Copy failed', severity: 'error' }));
  };

  const handleCSV = () => {
    if (!rows.length) return setSnack({ open: true, msg: 'No daily data to export', severity: 'info' });
    const headers = ['date', 'daily_basic', 'daily_tip', 'daily_svc', 'daily_total', 'half_day', 'punishment_half_day', 'no_tips', 'no_service'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.join(',')]
      .concat(
        rows.map((d) =>
          [
            d.date,
            fmt(d.daily_basic),
            fmt(d.daily_tip),
            fmt(d.daily_svc),
            fmt(d.daily_total),
            d.flag_half ? 1 : 0,
            d.flag_punishment_half ? 1 : 0,
            d.flag_no_tips ? 1 : 0,
            d.flag_no_svc ? 1 : 0,
          ]
            .map(escape)
            .join(',')
        )
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `salary_${slip.staff_name || staffId}_${year}-${mStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadReceiptPdf = async () => {
    if (!receiptModel) return;
    try {
      const { jsPDF } = await import('jspdf');
      const pageWidth = 80;
      const margin = 4;
      const innerWidth = pageWidth - (margin * 2);
      const statRows = Math.ceil(receiptModel.stats.length / 2);
      const pageHeight = Math.max(
        190,
        56
          + (statRows * 14)
          + (receiptModel.earningsRows.length * 6.8)
          + (receiptModel.deductionRows.length * 6.8)
          + (receiptModel.optionRows.length * 6.8)
          + (Math.max(1, receiptModel.advanceRows.length) * 8.5)
          + (Math.max(1, receiptModel.dailyRows.length) * 10.5)
      );

      const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: [pageWidth, pageHeight],
      });

      const color = (name) => RECEIPT_THEME[name];
      const setFill = (name) => doc.setFillColor(...color(name));
      const setDraw = (name) => doc.setDrawColor(...color(name));
      const setText = (name) => doc.setTextColor(...color(name));
      const moneyLabel = (value, isMoney = true) => formatReceiptMoneyLabel(value, isMoney);

      let y = 0;

      doc.setFillColor(...color('primary'));
      doc.rect(0, 0, pageWidth, 29, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11.5);
      doc.text(APP_NAME, pageWidth / 2, 8.5, { align: 'center' });
      doc.setFont('times', 'italic');
      doc.setFontSize(8.8);
      doc.text(documentLabel, pageWidth / 2, 13.8, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10.5);
      doc.text(receiptModel.staffName || '-', pageWidth / 2, 20.5, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.3);
      doc.text(receiptModel.branchName, pageWidth / 2, 24.8, { align: 'center' });
      doc.text(`Period ${receiptModel.periodLabel}`, pageWidth / 2, 27.8, { align: 'center' });
      y = 33;

      setFill('surface');
      setDraw('border');
      doc.roundedRect(margin, y, innerWidth, 10, 2, 2, 'FD');
      setText('ink');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.2);
      doc.text('Generated', margin + 2, y + 3.8);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.8);
      doc.text(receiptModel.generatedAt, margin + 2, y + 8);
      doc.setFont('helvetica', 'bold');
      doc.text('Branch', pageWidth - margin - 20, y + 3.8);
      doc.setFont('helvetica', 'normal');
      doc.text(receiptModel.branchName, pageWidth - margin - 2, y + 8, { align: 'right' });
      y += 13.5;

      const cardGap = 2;
      const cardWidth = (innerWidth - cardGap) / 2;
      const cardHeight = 11.5;
      receiptModel.stats.forEach((stat, index) => {
        const row = Math.floor(index / 2);
        const col = index % 2;
        const x = margin + (col * (cardWidth + cardGap));
        const top = y + (row * (cardHeight + 2));
        setFill('primarySoft');
        setDraw('border');
        doc.roundedRect(x, top, cardWidth, cardHeight, 2, 2, 'FD');
        setText('primary');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5.4);
        doc.text(String(stat.label || '').toUpperCase(), x + 2, top + 3.9);
        setText('ink');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(String(stat.value || '-'), x + 2, top + 8.7);
      });
      y += (statRows * (cardHeight + 2)) + 1;

      doc.setFillColor(...color('primary'));
      doc.roundedRect(margin, y, innerWidth, 11.5, 2.5, 2.5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.3);
      doc.text('TOTAL EARNED', margin + 2.2, y + 4.2);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11.8);
      doc.text(formatReceiptMoneyLabel(receiptModel.grossTotal), pageWidth - margin - 2, y + 8.2, { align: 'right' });
      y += 15.5;

      const drawSectionTitle = (title) => {
        setFill('primarySoft');
        doc.roundedRect(margin, y, innerWidth, 5.8, 1.8, 1.8, 'F');
        setText('primary');
        doc.setFont('times', 'bold');
        doc.setFontSize(7.3);
        doc.text(title, margin + 2, y + 3.9);
        y += 7.2;
      };

      const drawValueRows = (sectionTitle, sectionRows) => {
        drawSectionTitle(sectionTitle);
        sectionRows.forEach((row, index) => {
          const fillName = row.tone === 'total' ? 'primarySoft' : (index % 2 ? 'surfaceAlt' : 'surface');
          const valueTone = row.tone === 'negative'
            ? 'danger'
            : row.tone === 'positive'
              ? 'success'
              : row.tone === 'total'
                ? 'primary'
                : 'ink';
          setFill(fillName);
          setDraw('border');
          doc.roundedRect(margin, y, innerWidth, 6.1, 1.5, 1.5, 'FD');
          setText(row.tone === 'total' ? 'primary' : 'ink');
          doc.setFont('helvetica', row.tone === 'total' ? 'bold' : 'normal');
          doc.setFontSize(6.8);
          doc.text(row.label, margin + 2, y + 4);
          setText(valueTone);
          doc.setFont('helvetica', 'bold');
          doc.text(moneyLabel(row.value, row.isMoney), pageWidth - margin - 2, y + 4, { align: 'right' });
          y += 7;
        });
        y += 1;
      };

      const drawDetailTable = (sectionTitle, sectionRows, amountHeader, detailHeader, emptyLabel) => {
        drawSectionTitle(sectionTitle);
        const dateWidth = 18;
        const amountWidth = 19;
        const detailWidth = innerWidth - dateWidth - amountWidth;

        doc.setFillColor(...color('primary'));
        doc.setDrawColor(...color('primary'));
        doc.rect(margin, y, innerWidth, 6, 'FD');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.2);
        doc.text('Date', margin + 1.5, y + 4);
        doc.text(amountHeader, margin + dateWidth + amountWidth - 1.5, y + 4, { align: 'right' });
        doc.text(detailHeader, margin + dateWidth + amountWidth + 1.5, y + 4);
        y += 6;

        const rowsToRender = sectionRows.length
          ? sectionRows
          : [{ date: '-', amount: '-', details: emptyLabel, isMoney: false }];

        rowsToRender.forEach((row, index) => {
          const wrappedDetails = doc.splitTextToSize(String(row.details || '-'), detailWidth - 3);
          const rowHeight = Math.max(6.8, 3.2 + (wrappedDetails.length * 3.2));
          doc.setFillColor(...color(index % 2 ? 'surfaceAlt' : 'surface'));
          doc.setDrawColor(...color('border'));
          doc.rect(margin, y, innerWidth, rowHeight, 'FD');
          doc.line(margin + dateWidth, y, margin + dateWidth, y + rowHeight);
          doc.line(margin + dateWidth + amountWidth, y, margin + dateWidth + amountWidth, y + rowHeight);

          setText('ink');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6.1);
          doc.text(String(row.date || '-'), margin + 1.5, y + 4);
          doc.setFont('helvetica', 'bold');
          doc.text(moneyLabel(row.amount, row.isMoney !== false), margin + dateWidth + amountWidth - 1.4, y + 4, { align: 'right' });
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(5.8);
          wrappedDetails.forEach((line, lineIndex) => {
            doc.text(String(line), margin + dateWidth + amountWidth + 1.5, y + 3.7 + (lineIndex * 3.1));
          });

          y += rowHeight;
        });
        y += 1.5;
      };

      drawValueRows('Earnings', receiptModel.earningsRows);
      drawValueRows('Deductions', receiptModel.deductionRows);

      const netFill = receiptModel.netTone === 'negative' ? 'danger' : 'success';
      doc.setFillColor(...color(netFill));
      doc.roundedRect(margin, y, innerWidth, 11.5, 2.5, 2.5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.3);
      doc.text('NET SALARY', margin + 2.2, y + 4.2);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11.8);
      doc.text(formatReceiptMoneyLabel(receiptModel.netPay), pageWidth - margin - 2, y + 8.2, { align: 'right' });
      y += 15.5;

      drawValueRows('Applied Options', receiptModel.optionRows);
      drawDetailTable('Advance History', receiptModel.advanceRows, 'Amount', 'Details', 'No advance deductions');
      drawDetailTable('Daily Breakdown', receiptModel.dailyRows, 'Total', 'Breakdown', 'No daily rows');

      const safeName = String(receiptModel.staffName || 'staff').trim().replace(/[^a-z0-9_-]+/gi, '_');
      doc.save(`salary_slip_80mm_${safeName}_${year}_${mStr}.pdf`);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, msg: '80mm PDF download failed', severity: 'error' });
    }
  };

  const printReceipt80mm = () => {
    if (!receiptModel) return;

    const renderValueRows = (sectionRows) => sectionRows.map((row) => (
      `<tr class="${escapeHtml(row.tone || '')} ${row.tone === 'total' ? 'total' : ''}">`
      + `<td>${escapeHtml(row.label)}</td>`
      + `<td class="amount">${escapeHtml(formatReceiptMoneyLabel(row.value, row.isMoney))}</td>`
      + `</tr>`
    )).join('');

    const renderDetailRows = (sectionRows, emptyLabel, isMoney = true) => {
      if (!sectionRows.length) {
        return `<tr><td>-</td><td class="amount">-</td><td>${escapeHtml(emptyLabel)}</td></tr>`;
      }
      return sectionRows.map((row) => (
        `<tr>`
        + `<td>${escapeHtml(row.date || '-')}</td>`
        + `<td class="amount">${escapeHtml(formatReceiptMoneyLabel(row.amount, isMoney))}</td>`
        + `<td>${escapeHtml(row.details || '-')}</td>`
        + `</tr>`
      )).join('');
    };

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(documentLabel)} - ${escapeHtml(receiptModel.staffName || '')}</title>
  <style>
    @page { size: 80mm auto; margin: 2mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      width: 80mm;
      font-family: "Helvetica Neue", Arial, sans-serif;
      font-size: 10px;
      line-height: 1.4;
      color: #111827;
      background: #f3f5f7;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .wrap {
      width: 76mm;
      margin: 0 auto;
      background: #ffffff;
      border: 1px solid #dce4ea;
      border-radius: 14px;
      overflow: hidden;
    }
    .hero {
      padding: 9px 8px 8px;
      background: linear-gradient(135deg, #1b5e4a 0%, #10392c 100%);
      color: #ffffff;
      text-align: center;
    }
    .brand {
      font-family: Georgia, "Times New Roman", serif;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .doc {
      font-size: 8px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      opacity: 0.92;
      margin-top: 2px;
    }
    .staff {
      font-size: 14px;
      font-weight: 700;
      margin-top: 6px;
    }
    .meta {
      font-size: 9px;
      opacity: 0.92;
      margin-top: 3px;
    }
    .content {
      padding: 8px;
    }
    .meta-card {
      border: 1px solid #dce4ea;
      border-radius: 10px;
      background: #f8fafb;
      padding: 6px 7px;
      margin-bottom: 8px;
    }
    .meta-card-label {
      color: #1b5e4a;
      font-size: 8px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6px;
      margin-bottom: 8px;
    }
    .stat-card {
      border: 1px solid #d8e7df;
      border-radius: 10px;
      padding: 6px;
      background: #eff7f3;
    }
    .stat-card .label {
      font-size: 8px;
      color: #1b5e4a;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-weight: 700;
    }
    .stat-card .value {
      margin-top: 4px;
      font-size: 14px;
      font-weight: 700;
      color: #111827;
    }
    .net-banner {
      border-radius: 12px;
      padding: 7px 8px;
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: ${receiptModel.netTone === 'negative' ? '#b91c1c' : '#15803d'};
      margin-bottom: 8px;
    }
    .net-banner .label {
      font-size: 8px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      font-weight: 700;
      opacity: 0.95;
    }
    .net-banner .value {
      font-size: 16px;
      font-weight: 700;
    }
    .earn-banner {
      background: linear-gradient(135deg, #1b5e4a 0%, #134232 100%);
      margin-bottom: 8px;
    }
    .net-banner.negative {
      background: #b91c1c;
    }
    .net-banner.positive {
      background: #15803d;
    }
    .center { text-align: center; }
    .section {
      margin-bottom: 8px;
    }
    .section-title {
      display: inline-block;
      padding: 3px 8px;
      border-radius: 999px;
      border: 1px solid #d8e7df;
      background: #eff7f3;
      color: #1b5e4a;
      font-family: Georgia, "Times New Roman", serif;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 5px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
    .value-table td {
      border: 1px solid #dde5ea;
      padding: 5px 6px;
      font-size: 9px;
    }
    .value-table td.amount {
      text-align: right;
      white-space: nowrap;
      font-weight: 700;
    }
    .value-table tr:nth-child(even) td {
      background: #fafcfd;
    }
    .value-table tr.total td {
      background: #eff7f3;
      color: #1b5e4a;
      font-weight: 700;
    }
    .value-table tr.positive td.amount {
      color: #15803d;
    }
    .value-table tr.negative td.amount {
      color: #b91c1c;
    }
    .detail-table th,
    .detail-table td {
      border: 1px solid #dde5ea;
      padding: 5px 5px;
      vertical-align: top;
      font-size: 8.7px;
    }
    .detail-table th {
      background: #1b5e4a;
      color: #ffffff;
      font-size: 8px;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      text-align: left;
    }
    .detail-table th.amount,
    .detail-table td.amount {
      text-align: right;
      white-space: nowrap;
      width: 20mm;
      font-weight: 700;
    }
    .detail-table td:first-child {
      width: 18mm;
      font-weight: 700;
      white-space: nowrap;
    }
    .detail-table tr:nth-child(even) td {
      background: #fafcfd;
    }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="hero">
      <div class="brand">${escapeHtml(APP_NAME)}</div>
      <div class="doc">${escapeHtml(documentLabel)}</div>
      <div class="staff">${escapeHtml(receiptModel.staffName || '-')}</div>
      <div class="meta">${escapeHtml(receiptModel.branchName)}</div>
      <div class="meta">Period ${escapeHtml(receiptModel.periodLabel)}</div>
    </div>
    <div class="content">
      <div class="meta-card">
        <div class="meta-card-label">Generated</div>
        <div>${escapeHtml(receiptModel.generatedAt)}</div>
      </div>

      <div class="meta-grid">
        ${receiptModel.stats.map((stat) => (
          `<div class="stat-card"><div class="label">${escapeHtml(stat.label)}</div><div class="value">${escapeHtml(stat.value)}</div></div>`
        )).join('')}
      </div>

      <div class="net-banner earn-banner">
        <div class="label">Total Earned</div>
        <div class="value">${escapeHtml(formatReceiptMoneyLabel(receiptModel.grossTotal))}</div>
      </div>

      <div class="section">
        <div class="section-title">Earnings</div>
        <table class="value-table">
          <tbody>${renderValueRows(receiptModel.earningsRows)}</tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Deductions</div>
        <table class="value-table">
          <tbody>${renderValueRows(receiptModel.deductionRows)}</tbody>
        </table>
      </div>

      <div class="net-banner ${escapeHtml(receiptModel.netTone)}">
        <div class="label">Net Salary</div>
        <div class="value">${escapeHtml(formatReceiptMoneyLabel(receiptModel.netPay))}</div>
      </div>

      <div class="section">
        <div class="section-title">Applied Options</div>
        <table class="value-table">
          <tbody>${renderValueRows(receiptModel.optionRows)}</tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Advance History</div>
        <table class="detail-table">
          <thead>
            <tr><th>Date</th><th class="amount">Amount</th><th>Details</th></tr>
          </thead>
          <tbody>${renderDetailRows(receiptModel.advanceRows, 'No advance deductions')}</tbody>
        </table>
      </div>

      <div class="section">
        <div class="section-title">Daily Breakdown</div>
        <table class="detail-table">
          <thead>
            <tr><th>Date</th><th class="amount">Total</th><th>Breakdown</th></tr>
          </thead>
          <tbody>${renderDetailRows(receiptModel.dailyRows, 'No daily rows')}</tbody>
        </table>
      </div>
    </div>
  </div>
</body>
</html>`;

    const printWin = window.open('', '_blank', 'width=480,height=900');
    if (!printWin) {
      setSnack({ open: true, msg: 'Please allow popups for 80mm print', severity: 'warning' });
      return;
    }

    printWin.document.open();
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => {
      printWin.print();
    }, 250);
  };

  if (loading) {
    return (
      <Box maxWidth="1100px" mx="auto" p={2}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Skeleton variant="text" width={280} height={32} />
            <Skeleton variant="rectangular" width={260} height={36} />
          </Stack>
        </Paper>
        <Skeleton variant="rectangular" height={160} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={320} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxWidth="800px" mx="auto" p={2} textAlign="center">
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={reload}>Retry</Button>
      </Box>
    );
  }

  return (
    <Box maxWidth="1100px" mx="auto" p={{ xs: 1.5, sm: 2 }}>
      {/* Sticky header / actions */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1,
          p: { xs: 1.25, sm: 1.75 },
          mb: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.mode === 'light' ? '#ffffffee' : '#0b0f14ee',
          border: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(6px)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" gap={1.25}>
          <Stack direction="row" gap={1.25} alignItems="center" flexWrap="wrap">
            <Chip icon={<PersonIcon />} label={slip.staff_name} variant="outlined" />
            <Chip icon={<StorefrontIcon />} label={displayBranch} variant="outlined" />
            <Chip icon={<CalendarMonthIcon />} label={`${String(month).padStart(2, '0')}/${year}`} />
            {'designation' in (slip || {}) && slip.designation && (
              <Chip variant="outlined" label={String(slip.designation)} />
            )}
            <Chip color="primary" variant="outlined" label={`Total Earned: ${money(earnings?.gross)}`} />
            <Chip color={apiNet < 0 ? 'error' : 'success'} variant="outlined" label={`Net Salary: ${money(apiNet)}`} />
            {netMismatch && (
              <Chip color="warning" variant="outlined" label={`Calc Net: ${money(calcNet)}`} />
            )}
          </Stack>

          <Stack direction="row" gap={1}>
            <Tooltip title="Copy SMS summary">
              <span><IconButton onClick={handleCopySMS}><ContentCopyIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title="Download 80mm receipt PDF">
              <span><IconButton onClick={downloadReceiptPdf}><PictureAsPdfIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title="Export daily CSV">
              <span><IconButton onClick={handleCSV}><FileDownloadIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title="80mm receipt print">
              <span><IconButton onClick={printReceipt80mm}><PrintIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title="Print page">
              <span><IconButton onClick={() => window.print()}><PrintIcon /></IconButton></span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {/* Title */}
      <Box textAlign="center" sx={{ mb: 1 }}>
        <Typography variant="h4" fontWeight={800}>{APP_NAME} {documentLabel}</Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {slip.staff_name} — {String(month).padStart(2, '0')}/{year}
          {` — ${displayBranch}`}
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2,
          borderRadius: 3,
          color: '#fff',
          background: 'linear-gradient(135deg, #1b5e4a 0%, #10392c 100%)',
          boxShadow: '0 18px 45px rgba(16, 57, 44, 0.18)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1.5}
        >
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: '0.18em', opacity: 0.82 }}>
              Total Earned
            </Typography>
            <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1.05 }}>
              {money(earnings?.gross)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.86 }}>
              Gross salary before deductions
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' }} label={`Attendance: ${fmtDayCount(earnings?.daysPresent ?? 0)}`} />
            <Chip sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: '#fff' }} label={`Eligible: ${fmtDayCount(earnings?.eligibleDays ?? 0)}`} />
          </Stack>
        </Stack>
      </Paper>

      {/* SECTION 1 — Applied Options */}
      {applied && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Applied Options</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
            <Chip size="small" variant="outlined" label={`Full Days: ${fmtDayCount(applied.fullDays)}`} />
            <Chip size="small" icon={<AccessTimeIcon />} label={`Half Days: ${fmtDayCount(applied.halfDays)}`} />
            <Chip size="small" variant="outlined" label={`Normal Half: ${fmtDayCount(applied.normalHalfDays)}`} />
            <Chip size="small" color="warning" icon={<AccessTimeIcon />} label={`Punish Half: ${fmtDayCount(applied.punishmentHalfDays)}`} />
            <Chip size="small" icon={<MoneyOffIcon />} label={`No Tips: ${applied.noTipDays}`} color="warning" />
            <Chip size="small" icon={<DoNotDisturbAltIcon />} label={`No Service: ${applied.noSvcDays}`} color="error" />
            <Tooltip title="Zero values may also mean the pool was zero for that day.">
              <InfoOutlinedIcon fontSize="small" sx={{ opacity: .7, ml: 0.5 }} />
            </Tooltip>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip variant="outlined" label={`Tips Total: ${money(applied.tipTotal)}`} />
            <Chip variant="outlined" label={`Service Total: ${money(applied.svcTotal)}`} />
            <Chip color="warning" variant="outlined" label={`Punishment Half To Fund: ${money(applied.punishmentHalfDayFund)}`} />
          </Stack>
        </Paper>
      )}

      {/* SECTION 2 — Earnings + Deductions */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
          {/* Earnings */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Earnings</Typography>
            <Stack spacing={0.75}>
              <Row label="Days Attended" value={fmtDayCount(earnings?.daysPresent ?? 0)} />
              <Row label="Eligible Days" value={fmtDayCount(earnings?.eligibleDays ?? 0)} />
              <Row label="Punishment Half To Fund" value={money(earnings?.punishmentHalfDayFund)} />
              <Row label="Basic Earned" value={money(earnings?.basic)} />
              <Row label="Tips Share" value={money(earnings?.tips)} />
              <Row label="Service-Charge Share" value={money(earnings?.svc)} />
              <Row label={<span style={{ color: 'green' }}>Bonuses</span>} value={<span style={{ color: 'green' }}>+{money(earnings?.bonus)}</span>} />
              <Divider sx={{ my: 0.5 }} />
              <Row bold label={<strong>Gross Total</strong>} value={<strong>{money(earnings?.gross)}</strong>} />
            </Stack>
          </Box>

          <Divider flexItem orientation={isMobile ? 'horizontal' : 'vertical'} />

          {/* Deductions */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Deductions</Typography>
            <Stack spacing={0.75}>
              <Row label={<span style={{ color: 'red' }}>Penalties</span>} value={<span style={{ color: 'red' }}>−{money(deductions?.penalty)}</span>} />
              <Row label="Loan Deduction" value={`−${money(deductions?.loan)}`} />
              <Row label="Advance Deduction" value={`−${money(deductions?.advance)}`} />
              <Divider sx={{ my: 0.5 }} />
              <Row bold label={<strong>Total Deductions</strong>} value={<strong>{money(deductions?.total)}</strong>} />
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.25 },
          mb: 3,
          borderRadius: 3,
          borderColor: apiNet < 0 ? 'error.main' : 'success.main',
          background: apiNet < 0
            ? 'linear-gradient(135deg, rgba(185,28,28,0.08) 0%, rgba(185,28,28,0.02) 100%)'
            : 'linear-gradient(135deg, rgba(21,128,61,0.08) 0%, rgba(21,128,61,0.02) 100%)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1.5}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: '0.18em',
                color: apiNet < 0 ? 'error.main' : 'success.main',
              }}
            >
              Net Salary
            </Typography>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{ color: apiNet < 0 ? 'error.main' : 'success.main' }}
            >
              {money(apiNet)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Final amount after deductions
              {netMismatch ? ` (calc ${money(calcNet)})` : ''}
            </Typography>
          </Box>
          <Stack spacing={0.75} sx={{ minWidth: { sm: 220 } }}>
            <Row label="Total Earned" value={money(earnings?.gross)} />
            <Row label="Total Deductions" value={money(deductions?.total)} />
          </Stack>
        </Stack>
      </Paper>

      {/* Advance Salary History (categorized) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6">Advance Salary History</Typography>
          <Typography variant="body2" color="text.secondary">Total: {money(advanceHistory?.grand || 0)}</Typography>
        </Stack>

        {advanceHistory && advanceHistory.flat.length > 0 ? (
          <Stack gap={1}>
            {Object.entries(advanceHistory.groups).map(([cat, arr], i) => (
              <Accordion disableGutters key={cat + i} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" gap={1} alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
                    <Typography fontWeight={700}>{cat}</Typography>
                    <Chip size="small" variant="outlined" label={money(advanceHistory.catTotals[cat])} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 130 }}>Date</TableCell>
                        <TableCell align="right" sx={{ width: 140 }}>Amount</TableCell>
                        <TableCell>Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {arr.map((r, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{r.date || '-'}</TableCell>
                          <TableCell align="right">{money(r.amount)}</TableCell>
                          <TableCell>{r.note || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">No advance history.</Typography>
        )}
      </Paper>

      {/* Daily Breakdown */}
      <Typography variant="h6" gutterBottom>Daily Breakdown</Typography>

      {/* md+ table */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Basic</TableCell>
                  <TableCell align="right">Tips</TableCell>
                  <TableCell align="right">Service</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Flags</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((d, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{d.date}</TableCell>
                    <TableCell align="right">{fmt(d.daily_basic)}</TableCell>
                    <TableCell align="right">{fmt(d.daily_tip)}</TableCell>
                    <TableCell align="right">{fmt(d.daily_svc)}</TableCell>
                    <TableCell align="right">{fmt(d.daily_total)}</TableCell>
                    <TableCell>{renderFlags(d, slip)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* xs/sm card list */}
      <Stack sx={{ display: { xs: 'flex', md: 'none' } }} gap={1.25}>
        {rows.map((d, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography fontWeight={700}>{d.date}</Typography>
              <Typography variant="caption" color="text.secondary">Total: {money(d.daily_total)}</Typography>
            </Stack>
            <Stack direction="row" flexWrap="wrap" columnGap={3} rowgap={0.5}>
              <Cell label="Basic" value={money(d.daily_basic)} />
              <Cell label="Tips" value={money(d.daily_tip)} />
              <Cell label="Service" value={money(d.daily_svc)} />
            </Stack>
            <Box sx={{ mt: 1 }}>{renderFlags(d, slip)}</Box>
          </Paper>
        ))}
      </Stack>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ---------- Small presentational helpers ---------- */
function Row({ label, value, bold }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.25 }}>
      <Typography variant="body2" sx={{ opacity: .85 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 800 : 600}>{value}</Typography>
    </Stack>
  );
}

function Cell({ label, value }) {
  return (
    <Stack direction="row" gap={0.75} alignItems="center">
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}:</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Stack>
  );
}

function renderFlags(d, slip) {
  const perDay = Number(slip?.basic_salary_per_day || 0);
  const flagHalf = typeof d.flag_half !== 'undefined' ? !!d.flag_half : perDay > 0 && approxEq(d.daily_basic, perDay / 2);
  const flagPunishmentHalf = isPunishmentHalfFlag(d);
  const flagNoTips = typeof d.flag_no_tips !== 'undefined' ? !!d.flag_no_tips : Number(d.daily_tip) === 0;
  const flagNoSvc = typeof d.flag_no_svc !== 'undefined' ? !!d.flag_no_svc : Number(d.daily_svc) === 0;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {flagPunishmentHalf && (
        <Chip size="small" color="warning" icon={<AccessTimeIcon />} label={punishmentHalfLabel} variant="outlined" />
      )}
      {!flagPunishmentHalf && flagHalf && (
        <Chip size="small" icon={<AccessTimeIcon />} label="Half Day" variant="outlined" />
      )}
      {flagNoTips && (
        <Tooltip title={d.flag_no_tips === false ? 'Tips = 0 (pool zero)' : 'Tips excluded'}>
          <Chip size="small" color="warning" icon={<MoneyOffIcon />} label="No Tips" variant="outlined" />
        </Tooltip>
      )}
      {flagNoSvc && (
        <Tooltip title={d.flag_no_svc === false ? 'Service = 0 (pool zero)' : 'Service excluded'}>
          <Chip size="small" color="error" icon={<DoNotDisturbAltIcon />} label="No Service" variant="outlined" />
        </Tooltip>
      )}
      {!flagHalf && !flagPunishmentHalf && !flagNoTips && !flagNoSvc && (
        <Typography variant="caption" color="text.secondary">—</Typography>
      )}
    </Stack>
  );
}
